import React, { useState } from 'react';

interface PitchCanvasProps {
  className?: string;
  selectedPath?: Array<{ x: number; y: number }>;
  onPathChange?: (path: Array<{ x: number; y: number }>) => void;
}

interface Point {
  x: number;
  y: number;
}

const PitchCanvas: React.FC<PitchCanvasProps> = ({ className = '', selectedPath, onPathChange }) => {
  // Field dimensions
  const width = 1050;
  const height = 680;
  
  // Drawing state - use controlled path if provided, otherwise local state
  const [drawing, setDrawing] = useState(false);
  const [localPath, setLocalPath] = useState<Point[]>([]);
  
  // Use external path if provided, otherwise use local path
  const path = selectedPath || localPath;
  
  const updatePath = (newPath: Point[]) => {
    if (onPathChange) {
      onPathChange(newPath);
    } else {
      setLocalPath(newPath);
    }
  };
  
  // Realistic proportions based on 105m x 68m pitch at 10px per meter
  // Goal box (6-yard box): 5.5m depth x 18.32m width
  const sixYardDepth = 55; // px
  const sixYardWidth = 183; // px
  const sixYardY = (height - sixYardWidth) / 2;
  
  // Penalty area (18-yard box): 16.5m depth x 40.32m width
  const penaltyDepth = 165; // px
  const penaltyWidth = 403; // px
  const penaltyY = (height - penaltyWidth) / 2;
  
  // Center circle
  const centerCircleRadius = 91.5; // 9.15m
  const centerX = width / 2;
  const centerY = height / 2;
  
  // Penalty spot
  const penaltySpotSize = 4;
  const penaltySpot1Y = height / 2;
  const penaltySpot2Y = height / 2;
  const penaltySpot1X = 110; // 11m from goal line
  const penaltySpot2X = width - 110;
  
  // Center spot
  const centerSpotX = width / 2;
  const centerSpotY = height / 2;
  const centerSpotSize = 8;
  
  // Penalty arc radius (9.15m)
  const penaltyArcRadius = 91.5;
  
  // Compute arc end points where the circle around the penalty spot meets the penalty box line
  const leftPenaltyLineX = penaltyDepth;
  const rightPenaltyLineX = width - penaltyDepth;
  const dx = Math.abs(leftPenaltyLineX - penaltySpot1X);
  const dy = Math.sqrt(Math.max(penaltyArcRadius * penaltyArcRadius - dx * dx, 0));
  
  // Mouse event handlers for drawing
  const handleMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const scaleX = width / rect.width;
    const scaleY = height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    setDrawing(true);
    updatePath([{ x, y }]);
  };

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!drawing) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const scaleX = width / rect.width;
    const scaleY = height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    if (path.length === 0) {
      updatePath([{ x, y }]);
      return;
    }
    
    const last = path[path.length - 1];
    const dx = x - last.x;
    const dy = y - last.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    if (distance >= 5) {
      updatePath([...path, { x, y }]);
    }
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    setDrawing(false);
  };

  // Handle mouse up globally to allow dragging outside the SVG
  React.useEffect(() => {
    const handleMouseUpGlobal = () => {
      setDrawing(false);
    };
    
    if (drawing) {
      window.addEventListener('mouseup', handleMouseUpGlobal);
      return () => window.removeEventListener('mouseup', handleMouseUpGlobal);
    }
  }, [drawing]);
  
  return (
    <div className={`bg-gradient-to-br from-green-600 to-green-800 rounded-lg p-4 ${className}`}>
      <svg
        width="100%"
        height="auto"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        className="w-full cursor-crosshair"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        {/* Alternate light/dark grass stripes: 10 per half (total 20) */}
        <rect width={width} height={height} fill="#1c8f43" />
        {Array.from({ length: 20 }).map((_, i) => {
          const stripeWidth = width / 20;
          const x = i * stripeWidth;
          const isLight = i % 2 === 0;
          return (
            <rect
              key={i}
              x={x}
              y={0}
              width={stripeWidth}
              height={height}
              fill={isLight ? '#22984b' : '#1a7f3a'}
              opacity={isLight ? 0.35 : 0.32}
            />
          );
        })}
        
        {/* Outer field boundary */}
        <rect
          x="0"
          y="0"
          width={width}
          height={height}
          fill="none"
          stroke="#ffffff"
          strokeWidth="3"
        />
        
        {/* Center line */}
        <line
          x1={width / 2}
          y1="0"
          x2={width / 2}
          y2={height}
          stroke="#ffffff"
          strokeWidth="2"
        />
        
        {/* Center circle */}
        <circle
          cx={centerX}
          cy={centerY}
          r={centerCircleRadius}
          fill="none"
          stroke="#ffffff"
          strokeWidth="2"
        />
        
        {/* Center spot */}
        <circle
          cx={centerSpotX}
          cy={centerSpotY}
          r={centerSpotSize}
          fill="#ffffff"
        />
        
        {/* Left side - Goal Box (6-yard box) */}
        <rect
          x={0}
          y={sixYardY}
          width={sixYardDepth}
          height={sixYardWidth}
          fill="none"
          stroke="#ffffff"
          strokeWidth="2"
        />
        
        {/* Left side - Penalty Area (18-yard box) */}
        <rect
          x={0}
          y={penaltyY}
          width={penaltyDepth}
          height={penaltyWidth}
          fill="none"
          stroke="#ffffff"
          strokeWidth="2"
        />
        
        {/* Left side - Penalty Arc (the "D") */}
        <path
          d={`M ${leftPenaltyLineX} ${centerY - dy} A ${penaltyArcRadius} ${penaltyArcRadius} 0 0 1 ${leftPenaltyLineX} ${centerY + dy}`}
          fill="none"
          stroke="#ffffff"
          strokeWidth="2"
        />
        
        {/* Left side - Penalty Spot */}
        <circle
          cx={penaltySpot1X}
          cy={penaltySpot1Y}
          r={penaltySpotSize}
          fill="#ffffff"
        />
        
        {/* Right side - Goal Box (6-yard box) */}
        <rect
          x={width - sixYardDepth}
          y={sixYardY}
          width={sixYardDepth}
          height={sixYardWidth}
          fill="none"
          stroke="#ffffff"
          strokeWidth="2"
        />
        
        {/* Right side - Penalty Area (18-yard box) */}
        <rect
          x={width - penaltyDepth}
          y={penaltyY}
          width={penaltyDepth}
          height={penaltyWidth}
          fill="none"
          stroke="#ffffff"
          strokeWidth="2"
        />
        
        {/* Right side - Penalty Arc (the "D") */}
        <path
          d={`M ${rightPenaltyLineX} ${centerY - dy} A ${penaltyArcRadius} ${penaltyArcRadius} 0 0 0 ${rightPenaltyLineX} ${centerY + dy}`}
          fill="none"
          stroke="#ffffff"
          strokeWidth="2"
        />
        
        {/* Right side - Penalty Spot */}
        <circle
          cx={penaltySpot2X}
          cy={penaltySpot2Y}
          r={penaltySpotSize}
          fill="#ffffff"
        />

        {/* Corner arcs (1m radius) - curves should bend inward toward the field */}
        {/* Top-left corner */}
        <path d={`M 0 30 A 30 30 0 0 0 30 0`} fill="none" stroke="#ffffff" strokeWidth="2" />
        {/* Top-right corner */}
        <path d={`M ${width - 30} 0 A 30 30 0 0 0 ${width} 30`} fill="none" stroke="#ffffff" strokeWidth="2" />
        {/* Bottom-left corner */}
        <path d={`M 0 ${height - 30} A 30 30 0 0 1 30 ${height}`} fill="none" stroke="#ffffff" strokeWidth="2" />
        {/* Bottom-right corner */}
        <path d={`M ${width - 30} ${height} A 30 30 0 0 1 ${width} ${height - 30}`} fill="none" stroke="#ffffff" strokeWidth="2" />
        
        {/* Drawing path */}
        {path.length > 0 && (
          <>
            {/* Shadow for better visibility on green background */}
            {path.length > 1 && (
              <polyline
                points={path.map(p => `${p.x},${p.y}`).join(' ')}
                fill="none"
                stroke="#000000"
                strokeWidth="6"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.3"
              />
            )}
            {path.length > 0 && (
              <circle
                cx={path[path.length - 1].x}
                cy={path[path.length - 1].y}
                r="8"
                fill="#000000"
                opacity="0.3"
              />
            )}
            
            {/* Show polyline for multi-point paths */}
            {path.length > 1 && (
              <polyline
                points={path.map(p => `${p.x},${p.y}`).join(' ')}
                fill="none"
                stroke="#D9461F"
                strokeWidth="5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
            {/* Show dot for single point */}
            <circle
              cx={path[path.length - 1].x}
              cy={path[path.length - 1].y}
              r="7"
              fill="#D9461F"
              stroke="#FFFFFF"
              strokeWidth="4"
            />
          </>
        )}
      </svg>
    </div>
  );
};

export default PitchCanvas;

